import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Card, Container, Grid, IconButton, List, ListItem, Stack, SwipeableDrawer, TextField, Typography, Divider, Avatar, Switch } from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { alpha, useTheme } from '@mui/material/styles';
import { addSupplier, getSuppliers, updateSupplier } from '../../../redux/slices/stockManagementRedux/supplierRedux';
import { getAllFoods } from '../../../redux/slices/stockManagementRedux/foodRedux';
import { toast } from 'react-toastify';
import { accessVerify, capitalize, safeJSONParse } from '../../../utils/common';
import UploadMultiFile from '../../../components/upload/UploadMultiFile';
import { AnimatePresence, m } from 'framer-motion';
import Image from '../../../components/Image';
import ImageViewer from 'react-simple-image-viewer';
import { imageUpload } from '../../../inteceptor';
import PermissionRequired from '../../errorPages/permissionRequired';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Tab from '@mui/material/Tab';
import HistoryDiffViewer from './HistoryDiffViewer';

const PrettyContactCard = React.memo(({ value, onChange, onRemove, type }) => {
  const theme = useTheme();
  const onChangeFullName = useCallback((e) => onChange('fullName', e.target.value), [onChange]);
  const onChangeRegion = useCallback((e) => onChange('region', e.target.value), [onChange]);
  const onChangePosition = useCallback((e) => onChange('position', e.target.value), [onChange]);
  const onChangeEmail = useCallback((e) => onChange('email', e.target.value), [onChange]);
  const onChangePhone1 = useCallback((e) => onChange('phone1', e.target.value), [onChange]);
  const onChangePhone2 = useCallback((e) => onChange('phone2', e.target.value), [onChange]);
  const initialsFrom = (name) => {
    const n = String(name || '').trim();
    if (!n) return '•';
    const parts = n.split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  return (
    <Card sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none', background: theme.palette.background.paper }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', color: 'primary.dark', fontSize: 14 }}>{initialsFrom(value?.fullName)}</Avatar>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onRemove} size="small" sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
          <Iconify icon="solar:trash-bin-minimalistic-outline" width={18} height={18} />
        </IconButton>
      </Stack>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Full Name" value={value?.fullName || ''} onChange={onChangeFullName} />
        </Grid>
        {type === 'areaManager' ? (
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Region" value={value?.region || ''} onChange={onChangeRegion} />
          </Grid>
        ) : (
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Position" value={value?.position || ''} onChange={onChangePosition} />
          </Grid>
        )}
        <Grid item xs={12} md={5}>
          <TextField fullWidth label="Email" value={value?.email || ''} onChange={onChangeEmail} />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField fullWidth label="Phone Number" value={value?.phone1 || ''} onChange={onChangePhone1} />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField fullWidth label="Phone Number" value={value?.phone2 || ''} onChange={onChangePhone2} />
        </Grid>
      </Grid>
    </Card>
  );
});
PrettyContactCard.displayName = 'PrettyContactCard';

const SuppliersList = () => {
  const { supplierType } = useParams();
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const dataModel = {
    id: '',
    type: supplierType || '',
    name: '',
    code: '',
    address: '',
    companyPhone: '',
    accountName: '',
    accountNumber: '',
    bankName: '',
    bankBranch: '',
    companyType: '',
    brNumber: '',
    tin: '',
    website: '',
    financeLandPhone: '',
    financeEmail: '',
    financeMobile: '',
    creditPeriod: '',
    chequeIssueName: '',
    otAccHolderName: '',
    otBranchCode: '',
    otAccountNumber: '',
    otAccountType: '',
    otBranch: '',
    bizExperience: '',
    bizClients: '',
    bizMainProducts: '',
    bizCertifications: '',
    bizHealthSafety: '',
    bizSupplyCapacity: '',
    bizEnvironmental: '',
    bizEmployeeTraining: '',
    bizDeliveryTime: '',
    bizShippingCapabilities: '',
    bizReturnRefund: '',
    orderFrequency: '',
    orderMOQ: '',
    orderHow: '',
    orderMethodCall: false,
    orderMethodSms: false,
    orderMethodEmail: false,
    reqDocBR: false,
    reqDocNIC: false,
    reqDocBankLetter: false,
    declarationText: '',
    financeHeadName: '',
    declarationDate: '',
    financeHeadNIC: '',
    additionalNotes: '',
    foodList: [],
    supplierDocumentsData: [],
    areaManagers: [],
    contactPersons: [],
    history: [],
    locations: [], // <-- location IDs
  };
  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]);
  const [files, setFiles] = useState([]);
  const [documentGallery, setDocumentGallery] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const { data, addData } = useSelector((state) => state.supplier);
  let allFoods;
  ({ allData: allFoods } = useSelector((state) => state.food));
  const [tab, setTab] = useState('supplierInfo');
  const editingRef = useRef(false);

  let allLocations;

  ({ data: allLocations } = useSelector((state) => state.location));

  const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const ensureCid = (arr, type) => (Array.isArray(arr) ? arr : []).map((x) => (x && (x.id || x._cid) ? x : { ...x, _cid: `${type}_${uid()}` }));
  const normalizeArray = (x) => (Array.isArray(x) ? x : []);
  const normalizeSupplier = (obj) => {
    const parsedFood = safeJSONParse(obj?.foodList);
    const foodIdsFromParsed = Array.isArray(parsedFood) ? parsedFood.map((y) => (typeof y === 'object' && y !== null ? y.foodId : y)).filter(Boolean) : [];
    const parsedDocs = safeJSONParse(obj?.supplierDocumentsData);
    const parsedAM = ensureCid(safeJSONParse(obj?.areaManagers), 'am');
    const parsedCP = ensureCid(safeJSONParse(obj?.contactPersons), 'cp');
    const parsedHistory = safeJSONParse(obj?.history);
    const parsedLocations = safeJSONParse(obj?.locations);

    return {
      ...dataModel,
      ...Object.fromEntries(Object.keys(dataModel).map((k) => [k, obj?.[k] ?? dataModel[k]])),
      type: obj?.type || supplierType || '',
      foodList: Array.isArray(obj?.foodList) ? obj.foodList : foodIdsFromParsed,
      supplierDocumentsData: Array.isArray(obj?.supplierDocumentsData) ? obj?.supplierDocumentsData : normalizeArray(parsedDocs),
      areaManagers: Array.isArray(obj?.areaManagers) ? ensureCid(obj?.areaManagers, 'am') : normalizeArray(parsedAM),
      contactPersons: Array.isArray(obj?.contactPersons) ? ensureCid(obj?.contactPersons, 'cp') : normalizeArray(parsedCP),
      history: Array.isArray(obj?.history) ? obj.history : normalizeArray(parsedHistory),
      locations: Array.isArray(obj?.locations) ? obj.locations : Array.isArray(parsedLocations) ? parsedLocations : [],
    };
  };

  useEffect(() => {
    dispatch(getSuppliers(supplierType));
    dispatch(getAllFoods());
  }, [supplierType]);

  useEffect(() => {
    if (Array.isArray(data)) {
      const list = data.map((v) => ({ ...v })).filter((v) => v?.type === supplierType);
      setSuppliersList(list);
    } else {
      setSuppliersList([]);
    }
    if (!editingRef.current) setIsModelOpen(false);
  }, [data, supplierType]);

  useEffect(() => {
    setDocumentGallery((selectedDataObj?.supplierDocumentsData || []).map((v) => v?.url).filter(Boolean));
  }, [selectedDataObj?.supplierDocumentsData]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getSuppliers(supplierType));
    }
  }, [addData, supplierType]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
      editingRef.current = false;
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      editingRef.current = true;
      if (type === 'add') {
        setSelectedDataObj({ ...dataModel, type: supplierType || '' });
        setDocumentGallery([]);
        setFiles([]);
        setTab('supplierInfo');
      } else {
        const n = normalizeSupplier(modelData || {});
        setSelectedDataObj(n);
        setDocumentGallery(normalizeArray(safeJSONParse(modelData?.supplierDocumentsData)).map((v) => v?.url).filter(Boolean));
        setFiles([]);
        setTab('supplierInfo');
      }
    }
  };

  const updateEditingData = useCallback((key, value) => {
    editingRef.current = true;
    setSelectedDataObj((p) => ({
      ...p,
      [key]:
        value ??
        (Array.isArray(dataModel[key]) ? [] : typeof dataModel[key] === 'boolean' ? false : typeof dataModel[key] === 'string' ? '' : dataModel[key])
    }));
  }, [dataModel]);

  const addArrayItem = useCallback((key, emptyObj) => {
    editingRef.current = true;
    const withId = { ...emptyObj, _cid: `${key}_${uid()}` };
    setSelectedDataObj((p) => ({ ...p, [key]: [...(Array.isArray(p[key]) ? p[key] : []), withId] }));
  }, []);

  const updateArrayItem = useCallback((key, index, field, value) => {
    editingRef.current = true;
    setSelectedDataObj((p) => {
      const arr = Array.isArray(p[key]) ? p[key] : [];
      const next = arr.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        return updated;
      });
      return { ...p, [key]: next };
    });
  }, []);

  const removeArrayItem = useCallback((key, index) => {
    editingRef.current = true;
    setSelectedDataObj((p) => {
      const arr = Array.isArray(p[key]) ? p[key] : [];
      const next = arr.filter((_, i) => i !== index);
      return { ...p, [key]: next };
    });
  }, []);

  const initialsFrom = (name) => {
    const n = String(name || '').trim();
    if (!n) return '•';
    const parts = n.split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const uploadFiles = (file) => {
    return new Promise((resolve, reject) => {
      imageUpload(
        file,
        `SupplierDocuments`,
        (res) => {
          if (res?.status) resolve(res);
          else reject(new Error('Upload failed'));
        },
        (err) => {
          reject(new Error(err || 'Upload failed'));
        }
      );
    });
  };

  const handleImageUpload = async () => {
    setIsUploading(true);
    const snapshot = [...files];
    for (const file of snapshot) {
      try {
        setFiles((prev) => prev.map((f) => (f?.id === file?.id ? { ...f, isUploading: true } : f)));
        const res = await uploadFiles(file);
        setFiles((prev) => prev.filter((f) => f?.id !== file?.id));
        if (res?.status) {
          setSelectedDataObj((prev) => ({
            ...prev,
            supplierDocumentsData: [...(prev.supplierDocumentsData || []), { name: file?.name || '', url: res?.url }]
          }));
        }
      } catch {
        setFiles((prev) => prev.map((f) => (f?.id === file?.id ? { ...f, isUploading: false } : f)));
      }
    }
    setIsUploading(false);
  };

  const onSave = () => {
    const v = normalizeSupplier(selectedDataObj);
    if ((v.name || '').length === 0) toast.error('Supplier name is required');
    else if ((v.code || '').length === 0) toast.error('Supplier code is required');
    else if ((v.accountName || '').length === 0) toast.error('Account name is required');
    else if ((v.accountNumber || '').length === 0) toast.error('Account number is required');
    else if ((v.bankName || '').length === 0) toast.error('Bank name is required');
    else if ((v.bankBranch || '').length === 0) toast.error('Bank branch is required');
    else {
      const payload = {
        ...v,
        type: supplierType || v.type || '',
        declarationDate: v.declarationDate ? dayjs(v.declarationDate).format('YYYY-MM-DD') : null,
        foodList: Array.isArray(v.foodList) ? v.foodList.filter(Boolean) : [],
        supplierDocumentsData: Array.isArray(v.supplierDocumentsData) ? v.supplierDocumentsData.filter((d) => d && d.url) : [],
        areaManagers: Array.isArray(v.areaManagers)
          ? v.areaManagers
              .map(({ id, _cid, fullName, region, email, phone1, phone2 }) => ({ id, fullName, region, email, phone1, phone2 }))
              .filter((x) => x && (x.fullName || x.email || x.phone1 || x.phone2))
          : [],
        contactPersons: Array.isArray(v.contactPersons)
          ? v.contactPersons
              .map(({ id, _cid, fullName, position, email, phone1, phone2 }) => ({ id, fullName, position, email, phone1, phone2 }))
              .filter((x) => x && (x.fullName || x.email || x.phone1 || x.phone2))
          : [],
        locations: Array.isArray(v.locations) ? v.locations : [],
      };
      editingRef.current = false;
      if (isAdd) dispatch(addSupplier(payload));
      else dispatch(updateSupplier(payload));
    }
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const columns = !suppliersList?.[0]
    ? []
    : Object.keys(suppliersList?.[0])
      .map((k) => {
        if (['foodList', 'supplierDocumentsData', 'areaManagers', 'contactPersons', 'history'].includes(k)) return {};
        return {
          accessorKey: k,
          header: capitalize(k),
          Cell: ({ row }) => {
            const val = row?.original?.[k];
            return val === null || val === undefined || val === '' ? '-' : String(val);
          }
        };
      })
      .filter((c) => c?.accessorKey);

  const [rowSelection, setRowSelection] = useState(false);

  const HistoryRow = ({ h, idx }) => {
    const [open, setOpen] = useState(false);
    const jsonObj = useMemo(() => {
      if (!h?.json) return {};
      if (typeof h.json === 'object') return h.json;
      const parsed = safeJSONParse(h.json);
      return parsed && typeof parsed === 'object' ? parsed : {};
    }, [h?.json]);
    return (
      <Card key={`hist-${h?.id || idx}`} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Avatar sx={{ width: 28, height: 28 }}>{initialsFrom(h?.userName)}</Avatar>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>{h?.userName || 'Unknown'}</Typography>
          <Typography variant="caption" color="text.secondary">{h?.dateTime}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" onClick={() => setOpen((p) => !p)}>{open ? 'Hide JSON' : 'Show JSON'}</Button>
        </Stack>
        {open && (
          <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'background.neutral', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
            {JSON.stringify(jsonObj, null, 2)}
          </Box>
        )}
      </Card>
    );
  };

  const renderAreaManagers = useMemo(() => {
    return (selectedDataObj?.areaManagers || []).map((am, i) => {
      const key = am?.id ?? am?._cid;
      return (
        <PrettyContactCard
          key={key}
          value={am}
          type="areaManager"
          onChange={(field, v) => updateArrayItem('areaManagers', i, field, v)}
          onRemove={() => removeArrayItem('areaManagers', i)}
        />
      );
    });
  }, [selectedDataObj?.areaManagers, updateArrayItem, removeArrayItem]);

  const renderRepresentatives = useMemo(() => {
    return (selectedDataObj?.contactPersons || []).map((cp, i) => {
      const key = cp?.id ?? cp?._cid;
      return (
        <PrettyContactCard
          key={key}
          value={cp}
          type="representative"
          onChange={(field, v) => updateArrayItem('contactPersons', i, field, v)}
          onRemove={() => removeArrayItem('contactPersons', i)}
        />
      );
    });
  }, [selectedDataObj?.contactPersons, updateArrayItem, removeArrayItem]);

  const foodOptions = useMemo(() => {
    const list = Array.isArray(allFoods) ? allFoods : [];
    return list.map((f) => {
      const id = f?.id ?? f?.foodId ?? null;
      const label = f?.foodName || f?.name || (id != null ? `#${id}` : 'Unknown');
      return { label, value: id };
    }).filter((o) => o.value != null);
  }, [allFoods]);

  const selectedFoodOptions = useMemo(() => {
    const ids = new Set((selectedDataObj?.foodList || []).map((x) => Number(x)));
    return foodOptions.filter((o) => ids.has(Number(o.value)));
  }, [foodOptions, selectedDataObj?.foodList]);

    const isNotDeleted = (name = '') =>
    !String(name || '')
      .toLowerCase()
      .includes('delete');

  return (
    <Page title={`Suppliers: ${capitalize(supplierType || '')}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Suppliers: ${capitalize(supplierType || '')}`}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Suppliers', href: '' }, { name: capitalize(supplierType || '') }]}
          action={
            <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageModel(dataModel, 'add')}>
              New Supplier
            </Button>
          }
        />

        {accessVerify('SUPPLIER_VIEW') ? (
          <DataGridTable
            name={'Suppliers'}
            data={suppliersList}
            column={columns}
            isRowClickable
            onRowClick={(row) => manageModel(row?.original, 'edit')}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            renderRowActionItems={() => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{ sx: { width: '90%', height: '90%', ml: '5%', mt: '3%', borderRadius: '10px' } }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='h5'>{isAdd ? 'Add' : 'Edit'} Supplier</Typography>
            <Stack direction='row' spacing={1}>
              <LoadingButton variant='contained' size='medium' onClick={onSave}>
                {isAdd ? 'Add Supplier' : 'Save Changes'}
              </LoadingButton>
              <Button color='info' variant='outlined' size='medium' onClick={() => setIsModelOpen(false)}>
                Close
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={1} sx={{ px: 2, pb: 2 }}>
            <Grid item xs={12}>
              <Card sx={{ p: 0 }}>
                <TabContext value={tab}>
                  <Box sx={{ px: 0, pt: 0, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={(_, v) => setTab(v)} variant='fullWidth'>
                      <Tab label='Supplier Information' value='supplierInfo' />
                      <Tab label='Business & Product Info' value='business' />
                      <Tab label='Area Manager’s Contact' value='areaManager' />
                      <Tab label='Locations' value='locations' />
                      <Tab label='Representative Person’s Info' value='representative' />
                      <Tab label='Banking – Cheque Payments' value='bankCheque' />
                      <Tab label='Banking – Online Transfer' value='bankOnline' />
                      <Tab label='Finance Dept. Contact' value='financeContact' />
                      <Tab label='Declaration & Signature' value='declaration' />
                      <Tab label='Additional Comments / Attachments' value='attachments' />
                      <Tab label='History' value='history' />
                    </TabList>
                  </Box>

                  <TabPanel value='supplierInfo' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={3} maxWidth={900} mx='auto'>
                      <TextField fullWidth label='Supplier Type' value={supplierType || ''} disabled />
                      <TextField fullWidth label='Company Name' value={selectedDataObj?.name || ''} onChange={(e) => updateEditingData('name', e.target.value)} />
                      <TextField fullWidth label='Supplier Code' value={selectedDataObj?.code || ''} onChange={(e) => updateEditingData('code', e.target.value)} />
                      <TextField fullWidth label='Business Address' value={selectedDataObj?.address || ''} onChange={(e) => updateEditingData('address', e.target.value)} />
                      <TextField fullWidth label='Company Phone' value={selectedDataObj?.companyPhone || ''} onChange={(e) => updateEditingData('companyPhone', e.target.value)} />
                      <Autocomplete
                        options={['Sole Trade', 'Partnership', 'Company', 'Non Registered'].map((v) => ({ label: v, value: v }))}
                        value={selectedDataObj?.companyType ? { label: selectedDataObj.companyType, value: selectedDataObj.companyType } : null}
                        onChange={(e, nv) => updateEditingData('companyType', nv?.value || '')}
                        renderInput={(params) => <TextField {...params} label='Company Type' />}
                      />
                      <TextField fullWidth label='B/R Number' value={selectedDataObj?.brNumber || ''} onChange={(e) => updateEditingData('brNumber', e.target.value)} />
                      <TextField fullWidth label='TIN' value={selectedDataObj?.tin || ''} onChange={(e) => updateEditingData('tin', e.target.value)} />
                      <TextField fullWidth label='Website' value={selectedDataObj?.website || ''} onChange={(e) => updateEditingData('website', e.target.value)} />
                    </Stack>
                  </TabPanel>

                  <TabPanel value='financeContact' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={3} maxWidth={900} mx='auto'>
                      <TextField fullWidth label='Land Phone' value={selectedDataObj?.financeLandPhone || ''} onChange={(e) => updateEditingData('financeLandPhone', e.target.value)} />
                      <TextField fullWidth label='Email Address' value={selectedDataObj?.financeEmail || ''} onChange={(e) => updateEditingData('financeEmail', e.target.value)} />
                      <TextField fullWidth label='Mobile Number' value={selectedDataObj?.financeMobile || ''} onChange={(e) => updateEditingData('financeMobile', e.target.value)} />
                    </Stack>
                  </TabPanel>

                  <TabPanel value='areaManager' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={2} maxWidth={1000} mx='auto'>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='subtitle1'>Area Managers</Typography>
                        <IconButton
                          onClick={() => addArrayItem('areaManagers', { fullName: '', region: '', email: '', phone1: '', phone2: '' })}
                          size='small'
                          sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
                        >
                          <Iconify icon='eva:plus-fill' width={18} height={18} />
                        </IconButton>
                      </Box>
                      <Divider />
                      <Stack spacing={2}>
                        {(selectedDataObj?.areaManagers || []).length === 0 ? (
                          <Card sx={{ p: 2, borderRadius: 2, border: `1px dashed ${theme.palette.divider}`, boxShadow: 'none', bgcolor: 'background.neutral' }}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                              <Iconify icon='solar:user-plus-outline' width={20} height={20} />
                              <Typography variant='body2'>No area managers added</Typography>
                            </Stack>
                          </Card>
                        ) : null}
                        {renderAreaManagers}
                      </Stack>
                    </Stack>
                  </TabPanel>

                  <TabPanel value='locations' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={2} maxWidth={900} mx='auto'>
                      <Typography variant='subtitle1'>Tagged Locations</Typography>
                      <Divider />

                      <Grid container spacing={2}>
                        {(allLocations || []).map((loc) => {
                          const checked = selectedDataObj.locations.includes(loc.id);

                          return (
                            <Grid item xs={12} sm={6} md={4} key={loc.id}>
                              <Card
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: checked ? 'primary.main' : 'divider',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  updateEditingData('locations', checked ? selectedDataObj.locations.filter((id) => id !== loc.id) : [...selectedDataObj.locations, loc.id]);
                                }}
                              >
                                <Stack direction='row' alignItems='center' spacing={2}>
                                  <Typography variant='body2' sx={{ flexGrow: 1 }}>
                                    {loc.name}
                                  </Typography>
                                  <Switch checked={checked} />
                                </Stack>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Stack>
                  </TabPanel>

                  <TabPanel value='representative' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={2} maxWidth={1000} mx='auto'>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='subtitle1'>Representatives</Typography>
                        <IconButton
                          onClick={() => addArrayItem('contactPersons', { fullName: '', position: '', email: '', phone1: '', phone2: '' })}
                          size='small'
                          sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
                        >
                          <Iconify icon='eva:plus-fill' width={18} height={18} />
                        </IconButton>
                      </Box>
                      <Divider />
                      <Stack spacing={2}>
                        {(selectedDataObj?.contactPersons || []).length === 0 ? (
                          <Card sx={{ p: 2, borderRadius: 2, border: `1px dashed ${theme.palette.divider}`, boxShadow: 'none', bgcolor: 'background.neutral' }}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                              <Iconify icon='solar:user-plus-outline' width={20} height={20} />
                              <Typography variant='body2'>No representatives added</Typography>
                            </Stack>
                          </Card>
                        ) : null}
                        {renderRepresentatives}
                      </Stack>
                    </Stack>
                  </TabPanel>

                  <TabPanel value='bankCheque' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={3} maxWidth={900} mx='auto'>
                      <TextField fullWidth label='Credit Period' value={selectedDataObj?.creditPeriod || ''} onChange={(e) => updateEditingData('creditPeriod', e.target.value)} />
                      <TextField fullWidth label='Name to Issue the Cheque' value={selectedDataObj?.chequeIssueName || ''} onChange={(e) => updateEditingData('chequeIssueName', e.target.value)} />
                    </Stack>
                  </TabPanel>

                  <TabPanel value='bankOnline' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={3} maxWidth={900} mx='auto'>
                      <TextField fullWidth label='Account Holder Name' value={selectedDataObj?.otAccHolderName || ''} onChange={(e) => updateEditingData('otAccHolderName', e.target.value)} />
                      <TextField fullWidth label='Branch Code' value={selectedDataObj?.otBranchCode || ''} onChange={(e) => updateEditingData('otBranchCode', e.target.value)} />
                      <TextField fullWidth label='Account Number' value={selectedDataObj?.otAccountNumber || ''} onChange={(e) => updateEditingData('otAccountNumber', e.target.value)} />
                      <Autocomplete
                        options={['Savings', 'Current', 'Other'].map((v) => ({ label: v, value: v }))}
                        value={selectedDataObj?.otAccountType ? { label: selectedDataObj.otAccountType, value: selectedDataObj.otAccountType } : null}
                        onChange={(e, nv) => updateEditingData('otAccountType', nv?.value || '')}
                        renderInput={(params) => <TextField {...params} label='Account Type' />}
                      />
                      <TextField fullWidth label='Branch' value={selectedDataObj?.otBranch || ''} onChange={(e) => updateEditingData('otBranch', e.target.value)} />
                      <TextField fullWidth label='Bank Account Name' value={selectedDataObj?.accountName || ''} onChange={(e) => updateEditingData('accountName', e.target.value)} />
                      <TextField fullWidth label='Bank Account Number' value={selectedDataObj?.accountNumber || ''} onChange={(e) => updateEditingData('accountNumber', e.target.value)} />
                      <TextField fullWidth label='Bank Name' value={selectedDataObj?.bankName || ''} onChange={(e) => updateEditingData('bankName', e.target.value)} />
                      <TextField fullWidth label='Branch Name' value={selectedDataObj?.bankBranch || ''} onChange={(e) => updateEditingData('bankBranch', e.target.value)} />
                    </Stack>
                  </TabPanel>

                  <TabPanel value='business' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={3} maxWidth={900} mx='auto'>
                      <TextField fullWidth label='Experience' value={selectedDataObj?.bizExperience || ''} onChange={(e) => updateEditingData('bizExperience', e.target.value)} />
                      <TextField fullWidth label='Clients' value={selectedDataObj?.bizClients || ''} onChange={(e) => updateEditingData('bizClients', e.target.value)} />
                      <TextField fullWidth label='Main Products Provided' value={selectedDataObj?.bizMainProducts || ''} onChange={(e) => updateEditingData('bizMainProducts', e.target.value)} />
                      <Autocomplete
                        multiple
                        options={foodOptions.filter((food) => isNotDeleted(food?.label))}
                        value={selectedFoodOptions}
                        onChange={(e, nv) =>
                          updateEditingData(
                            'foodList',
                            nv.map((x) => x.value)
                          )
                        }
                        renderInput={(params) => <TextField {...params} label='Linked Foods' placeholder='Type to search & select' />}
                        filterSelectedOptions
                        disableCloseOnSelect
                      />
                      <TextField
                        fullWidth
                        label='Certifications & Accreditations'
                        value={selectedDataObj?.bizCertifications || ''}
                        onChange={(e) => updateEditingData('bizCertifications', e.target.value)}
                      />
                      <TextField fullWidth label='Health & Safety Standards' value={selectedDataObj?.bizHealthSafety || ''} onChange={(e) => updateEditingData('bizHealthSafety', e.target.value)} />
                      <TextField fullWidth label='Supply Capacity' value={selectedDataObj?.bizSupplyCapacity || ''} onChange={(e) => updateEditingData('bizSupplyCapacity', e.target.value)} />
                      <TextField fullWidth label='Environmental Standards' value={selectedDataObj?.bizEnvironmental || ''} onChange={(e) => updateEditingData('bizEnvironmental', e.target.value)} />
                      <TextField
                        fullWidth
                        label='Employee Training Programs'
                        value={selectedDataObj?.bizEmployeeTraining || ''}
                        onChange={(e) => updateEditingData('bizEmployeeTraining', e.target.value)}
                      />
                      <TextField fullWidth label='Delivery Time' value={selectedDataObj?.bizDeliveryTime || ''} onChange={(e) => updateEditingData('bizDeliveryTime', e.target.value)} />
                      <TextField
                        fullWidth
                        label='Shipping & Delivery Capabilities'
                        value={selectedDataObj?.bizShippingCapabilities || ''}
                        onChange={(e) => updateEditingData('bizShippingCapabilities', e.target.value)}
                      />
                      <TextField fullWidth label='Return/Refund Policy' value={selectedDataObj?.bizReturnRefund || ''} onChange={(e) => updateEditingData('bizReturnRefund', e.target.value)} />
                      <Autocomplete
                        options={['Daily', 'Weekly', 'Monthly', 'Annually'].map((v) => ({ label: v, value: v }))}
                        value={selectedDataObj?.orderFrequency ? { label: selectedDataObj.orderFrequency, value: selectedDataObj.orderFrequency } : null}
                        onChange={(e, nv) => updateEditingData('orderFrequency', nv?.value || '')}
                        renderInput={(params) => <TextField {...params} label='Delivery Frequency' />}
                      />
                      <TextField fullWidth label='MOQ' value={selectedDataObj?.orderMOQ || ''} onChange={(e) => updateEditingData('orderMOQ', e.target.value)} />
                      {/*<TextField fullWidth label="How to Place Order" value={selectedDataObj?.orderHow || ''} onChange={(e) => updateEditingData('orderHow', e.target.value)} />*/}
                      <Autocomplete
                        multiple
                        options={[
                          { label: 'Call', value: 'call' },
                          { label: 'SMS', value: 'sms' },
                          { label: 'E-mail', value: 'email' },
                        ]}
                        value={[
                          selectedDataObj?.orderMethodCall ? { label: 'Call', value: 'call' } : null,
                          selectedDataObj?.orderMethodSms ? { label: 'SMS', value: 'sms' } : null,
                          selectedDataObj?.orderMethodEmail ? { label: 'E-mail', value: 'email' } : null,
                        ].filter(Boolean)}
                        onChange={(e, nv) => {
                          const vals = nv.map((x) => x.value);
                          updateEditingData('orderMethodCall', vals.includes('call'));
                          updateEditingData('orderMethodSms', vals.includes('sms'));
                          updateEditingData('orderMethodEmail', vals.includes('email'));
                        }}
                        renderInput={(params) => <TextField {...params} label='How to Place Order (Order Methods)' />}
                      />
                    </Stack>
                  </TabPanel>

                  <TabPanel value='declaration' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={3} maxWidth={900} mx='auto'>
                      <TextField fullWidth label='Finance Department Head Name' value={selectedDataObj?.financeHeadName || ''} onChange={(e) => updateEditingData('financeHeadName', e.target.value)} />
                      <TextField fullWidth label='NIC Number' value={selectedDataObj?.financeHeadNIC || ''} onChange={(e) => updateEditingData('financeHeadNIC', e.target.value)} />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label='Date'
                          value={selectedDataObj?.declarationDate ? dayjs(selectedDataObj.declarationDate) : null}
                          onChange={(newVal) => updateEditingData('declarationDate', newVal ? dayjs(newVal).format('YYYY-MM-DD') : '')}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                      <TextField
                        fullWidth
                        label='Declaration'
                        multiline
                        rows={3}
                        value={selectedDataObj?.declarationText || ''}
                        onChange={(e) => updateEditingData('declarationText', e.target.value)}
                      />
                    </Stack>
                  </TabPanel>

                  <TabPanel value='attachments' sx={{ p: 2 }} keepMounted>
                    <Stack spacing={2} maxWidth={1100} mx='auto'>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label='Additional Comments / Notes'
                        value={selectedDataObj?.additionalNotes || ''}
                        onChange={(e) => updateEditingData('additionalNotes', e.target.value)}
                      />
                      <UploadMultiFile
                        error={false}
                        showPreview
                        files={files}
                        onRemove={(removedFile) => setFiles((prev) => prev.filter((f) => f !== removedFile))}
                        onRemoveAll={() => setFiles(() => [])}
                        helperText={''}
                        onDrop={(uploadedFiles) => {
                          setFiles((prev) => [
                            ...prev,
                            ...uploadedFiles.map((file, index) => Object.assign(file, { id: `${file.name}-${Date.now()}-${index}`, preview: URL.createObjectURL(file), isUploading: false })),
                          ]);
                        }}
                        onUpload={handleImageUpload}
                        isUploading={isUploading}
                      />
                      {documentGallery?.filter((v) => v)?.length > 0 && (
                        <Typography variant='subtitle1' padding='10px 0'>
                          Uploaded Files
                        </Typography>
                      )}
                      <List disablePadding sx={{ my: 1 }}>
                        <AnimatePresence>
                          {documentGallery
                            ?.filter((v) => v)
                            ?.map((src, index) => (
                              <ListItem
                                key={`${src}${index}`}
                                component={m.div}
                                onClick={() => openImageViewer(index)}
                                sx={{
                                  p: 0,
                                  m: 0.5,
                                  width: 92,
                                  height: 92,
                                  borderRadius: 1.25,
                                  overflow: 'hidden',
                                  position: 'relative',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  border: (t) => `solid 1px ${t.palette.divider}`,
                                }}
                              >
                                <Image alt='preview' src={src} ratio='1/1' />
                                <IconButton
                                  size='small'
                                  sx={{
                                    top: 6,
                                    p: '2px',
                                    right: 6,
                                    position: 'absolute',
                                    color: 'common.white',
                                    bgcolor: (t) => alpha(t.palette.grey[900], 0.72),
                                    '&:hover': { bgcolor: (t) => alpha(t.palette.grey[900], 0.48) },
                                  }}
                                />
                              </ListItem>
                            ))}
                        </AnimatePresence>
                      </List>
                      {isViewerOpen && <ImageViewer src={documentGallery} currentIndex={currentImage} disableScroll={false} closeOnClickOutside onClose={closeImageViewer} />}
                    </Stack>
                  </TabPanel>

                  <TabPanel value='history' sx={{ p: 2 }} keepMounted>
                    <HistoryDiffViewer items={selectedDataObj?.history || []} />
                  </TabPanel>
                </TabContext>
              </Card>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
};

export default SuppliersList;
